import React, { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Copy, Download, Link2, Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";

export function AttachmentInput({ value, onChange, testid }) {
  const { t } = useLang();
  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (value.length + files.length > 3) {
      toast.error(t("forms.max_files"));
      return;
    }
    files.forEach((f) => {
      if (f.size > 1_500_000) {
        toast.error(`${f.name}: ${t("k.file_optional")}`);
        return;
      }
      const rd = new FileReader();
      rd.onload = () => onChange((prev) => [...prev, { filename: f.name, file_base64: rd.result }]);
      rd.readAsDataURL(f);
    });
    e.target.value = "";
  };
  return (
    <div data-testid={testid}>
      <label className="ec-label">{t("forms.attachments")}</label>
      <input className="ec-input" type="file" multiple onChange={onFiles} data-testid={`${testid}-input`} />
      {value.length > 0 && (
        <ul className="mt-2 space-y-1">
          {value.map((a, i) => (
            <li key={i} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2.5 py-1.5" data-testid={`${testid}-file-${i}`}>
              <span className="font-semibold text-slate-700 truncate">{a.filename}</span>
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700 ml-2 shrink-0" data-testid={`${testid}-remove-${i}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PublicLinkButton({ formType, testid }) {
  const { t } = useLang();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [qr, setQr] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [targetTid, setTargetTid] = useState("");

  const createLink = async (sendTo, tid) => {
    setBusy(true);
    try {
      const payload = { form_type: formType };
      if (tid) payload.tenant_id = tid;
      if (sendTo) payload.send_to = sendTo;
      const { data } = await api.post("/forms/public-links", payload);
      setLink(data);
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (link?.url) {
      QRCode.toDataURL(link.url, { width: 360, margin: 1, color: { dark: "#16335F" } }).then(setQr).catch(() => setQr(null));
    }
  }, [link]);

  const openModal = async () => {
    setOpen(true);
    if (["admin", "superadmin"].includes(user?.role)) {
      api.get("/forms/public-links/tenants").then((r) => setTenants(r.data)).catch(() => setTenants([]));
    }
    const ok = await createLink(null, targetTid);
    if (!ok) setOpen(false);
  };

  const onTargetChange = (v) => {
    setTargetTid(v);
    setLink(null);
    createLink(null, v);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success(t("forms.link_copied"));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const send = async () => {
    if (await createLink(email, targetTid)) {
      toast.success(t("forms.link_sent"));
      setEmail("");
    }
  };

  const revoke = async () => {
    try {
      await api.delete(`/forms/public-links/${link.link_id}`);
      toast.success(t("forms.revoked"));
      setLink(null);
      setOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (user?.role === "client") return null;

  return (
    <>
      <button onClick={openModal} data-testid={testid} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#033A8C] text-white text-sm font-bold hover:bg-[#044CBC] transition-colors">
        <Link2 className="w-4 h-4" /> {t("forms.public_link")}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="ec-card max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-modal`}>
            <h3 className="font-heading font-bold text-slate-900">{t("forms.link_title")}</h3>
            <p className="text-xs text-slate-500">{t("forms.link_help")}</p>
            {tenants.length > 0 && (
              <div>
                <label className="ec-label">{t("pf.link_target_org")}</label>
                <select className="ec-input" value={targetTid} onChange={(e) => onTargetChange(e.target.value)} data-testid={`${testid}-target-org`}>
                  <option value="">{t("pf.link_target_own")}</option>
                  {tenants.filter((x) => x.id !== user?.tenant_id).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
            )}
            {busy && !link ? (
              <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></div>
            ) : link && (
              <>
                <div className="flex gap-2">
                  <input className="ec-input font-mono text-xs" readOnly value={link.url} onFocus={(e) => e.target.select()} data-testid={`${testid}-url`} />
                  <button onClick={copy} data-testid={`${testid}-copy`} className="px-3 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold shrink-0 flex items-center gap-1">
                    <Copy className="w-3.5 h-3.5" /> {t("forms.copy")}
                  </button>
                </div>
                <div className="text-[11px] text-slate-400" data-testid={`${testid}-uses`}>{link.uses} {t("forms.uses")}</div>
                {qr && (
                  <div className="flex items-center gap-4 bg-slate-50 rounded-lg p-3">
                    <img src={qr} alt="QR" className="w-24 h-24 rounded border border-slate-200" data-testid={`${testid}-qr`} />
                    <div className="space-y-2">
                      <p className="text-[11px] text-slate-500">{t("forms.qr_hint")}</p>
                      <a href={qr} download={`kycfederal-qr-${formType}.png`} data-testid={`${testid}-qr-download`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold hover:bg-[#033A8C] transition-colors">
                        <Download className="w-3.5 h-3.5" /> {t("forms.qr_download")}
                      </a>
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <input className="ec-input" type="email" placeholder={t("auth.email")} value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`${testid}-email`} />
                  <button onClick={send} disabled={!email || busy} data-testid={`${testid}-send`} className="px-3 py-2 rounded-lg bg-[#044CBC] text-white text-xs font-bold shrink-0 flex items-center gap-1.5 disabled:opacity-60">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {t("forms.send_link")}
                  </button>
                </div>
                <button onClick={revoke} data-testid={`${testid}-revoke`} className="text-xs font-bold text-red-600 hover:text-red-800">
                  {t("forms.revoke")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
